import {
    GTMFormValues,
    gtmSchema,
    TrackingInfos,
    TrackingType
} from "@/app/stores/[id]/storefront/types/tracking";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface GTMFormProps {
  trackingConfig: TrackingInfos<TrackingType.GTM>;
  defaultValues: GTMFormValues;
  isLoading: boolean;
  savePixelConfig: (data: GTMFormValues) => void;
}

function GTMForm({
  trackingConfig,
  defaultValues,
  isLoading,
  savePixelConfig,
}: GTMFormProps) {
  const { t } = useTranslation('storefront');
  const form = useForm<GTMFormValues>({
    resolver: zodResolver(gtmSchema),
    defaultValues: defaultValues,
  });
  const { register, formState: { errors }, watch, setValue } = form

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

    return (
        <>
        <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="enabled" className="text-sm font-medium">
            {t('tracking.gtm.enable', { name: trackingConfig.name })}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('tracking.gtm.enableDescription')}
          </p>
        </div>
        <Switch
          id="enabled"
          checked={watch('enabled')}
          onCheckedChange={(checked) => {
            setValue('enabled', checked, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            savePixelConfig({ ...watch(), enabled: checked });
          }}
          disabled={isLoading}
        />
      </div>
      <div className="space-y-4">
          <h3 className="text-sm font-semibold">{t('tracking.gtm.pixelConfiguration')}</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pixelId">
                {t('tracking.gtm.containerId')}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="pixelId"
                placeholder={trackingConfig.containerIdPlaceholder }
                disabled={isLoading}
                {...register('containerId')}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue('containerId', value, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  savePixelConfig({ ...watch(), containerId: value });
                }}
              />
              <p className="text-xs text-muted-foreground">
                {trackingConfig.containerIdDescription}
              </p>
              {errors.containerId && (
                <p className="text-xs text-destructive">{errors.containerId.message}</p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">{t('tracking.gtm.googleAdsIntegration')}</h3>
          <p className="text-xs text-muted-foreground">
            {t('tracking.gtm.googleAdsDescription')}
          </p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="googleAdsConversionId">
                {t('tracking.gtm.conversionId')}
              </Label>
              <Input
                id="googleAdsConversionId"
                placeholder="AW-XXXXXXXXX"
                disabled={isLoading}
                {...register('googleAdsConversionId')}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue('googleAdsConversionId', value, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  savePixelConfig({ ...watch(), googleAdsConversionId: value });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="googleAdsConversionLabel">
                {t('tracking.gtm.conversionLabel')}
              </Label>
              <Input
                id="googleAdsConversionLabel"
                placeholder={t('tracking.gtm.conversionLabelPlaceholder')}
                disabled={isLoading}
                {...register('googleAdsConversionLabel')}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue('googleAdsConversionLabel', value, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  savePixelConfig({ ...watch(), googleAdsConversionLabel: value });
                }}
              />
              <p className="text-xs text-muted-foreground">
                {t('tracking.gtm.conversionLabelDescription')}
              </p>
            </div>
          </div>
        </div>
        {trackingConfig.events && <div className="space-y-4">
          <h3 className="text-sm font-semibold">{t('tracking.gtm.eventTracking')}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {trackingConfig.events.map((event) => {
              // event.key is already correctly typed based on the tracking type
              const eventKey = event.key;
              return (
                <div key={String(event.key)} className="flex items-center space-x-2">
                  <Checkbox
                    id={`event-${event.key}`}
                    checked={watch(`events.${eventKey}` as any)}
                    onCheckedChange={(checked) => {
                      setValue(`events.${eventKey}` as any, checked as boolean);
                      savePixelConfig({ ...watch(), events: { ...watch('events'), [eventKey]: checked } });
                    }}
                    disabled={isLoading}
                  />
                  <Label htmlFor={`event-${event.key}`} className="cursor-pointer font-normal">
                    {event.label}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>}
      </>
    );
}

export default GTMForm;